import { ActiveSubscriptionStatuses, SubscriptionPlan, WorkspaceMember, WorkspaceRole } from 'models/user'
import { useMemo, useState } from 'react'

import { ChangeMemberData } from './types'
import { Nullable } from 'models/helpers'
import constate from 'constate'
import { getDateString } from 'utils/time'
import { useTimezone } from 'components/contexts/timezone.context'
import { useUserData } from 'components/contexts/UserDataContext'

/** Hook setup with Workspaces Profile logic to be used with constate */
export const [WorkspacesProfileContextProvider, useWorkspacesProfileContext] = constate(({ workspaceId }: { workspaceId: string }) => {
  const { baseUserData, userWorkspacesQuery } = useUserData()
  const { userTimezone } = useTimezone()

  // Data to all workspaces the user is a member of
  const allUserWorkspacesResponse = userWorkspacesQuery
  const allUserWorkspacesData = useMemo(() => allUserWorkspacesResponse.data?.workspaces, [allUserWorkspacesResponse])

  // Current workspace profile data
  const workspaceData = useMemo(() => allUserWorkspacesData?.find((workspace) => workspace.workspaceId === workspaceId), [allUserWorkspacesData, workspaceId])
  const workspaceDataOfUser = useMemo(() => workspaceData?.members?.find((member) => member.userId === baseUserData?.id), [workspaceData, baseUserData])
  const workspaceCreationDate = useMemo(() => workspaceData && getDateString(workspaceData.creationDate, { timezone: userTimezone }), [workspaceData, userTimezone])
  const sortedWorkspaceMembers = useMemo(() => {
    if (!workspaceData?.members) return []

    return [...workspaceData.members].sort((a, b) => {
      const memberA = a.name.toLowerCase()
      const memberB = b.name.toLowerCase()
      if (memberA > memberB) return 1
      if (memberA < memberB) return -1
      return 0
    })
  }, [workspaceData])
  const canUserSeeMembersListSection = useMemo(() => workspaceData?.members !== null, [workspaceData?.members])
  const workspaceMembersWithAdminRole = useMemo(() => sortedWorkspaceMembers && sortedWorkspaceMembers.filter((member) => member.role === WorkspaceRole.ADMIN), [sortedWorkspaceMembers])
  const isSingleAdminInWorkspace = useMemo(() => workspaceMembersWithAdminRole?.length === 1, [workspaceMembersWithAdminRole])
  const hasWorkspaceActiveSubscription = useMemo(() => workspaceData && ActiveSubscriptionStatuses.has(workspaceData.workspaceStatus), [workspaceData])
  const workspaceMembersWithMemberRole = useMemo(() => sortedWorkspaceMembers && sortedWorkspaceMembers.filter((member) => member.role === WorkspaceRole.MEMBER), [sortedWorkspaceMembers])
  const workspaceMembersEmails = useMemo(() => sortedWorkspaceMembers?.map((member) => member.email), [sortedWorkspaceMembers])
  const pendingInvitations = useMemo(() => workspaceData && workspaceData.pendingInvitations, [workspaceData])
  const isWorkspaceEnterprise = useMemo(() => !!workspaceData && [SubscriptionPlan.ENTERPRISE, SubscriptionPlan.ENTERPRISE_PER_SEAT, SubscriptionPlan.ENTERPRISE_FIXED_RATE].includes(workspaceData.plan), [workspaceData])
  const userWorkspaceInvitedData = useMemo(() => pendingInvitations?.find((member) => member.email === baseUserData?.email), [baseUserData?.email, pendingInvitations])
  const userWorkspaceInvitedBy = useMemo(() => sortedWorkspaceMembers?.find((member) => member.userId === userWorkspaceInvitedData?.invitedBy), [sortedWorkspaceMembers, userWorkspaceInvitedData?.invitedBy])

  // member setting control
  const [addMembersPopup, setAddMembersPopup] = useState(false)
  const [removeMember, setRemoveMember] = useState<WorkspaceMember | undefined>(undefined)
  const [changeMemberRole, setChangeMemberRole] = useState<Nullable<ChangeMemberData>>(null)

  return {
    workspaceId,
    allUserWorkspacesResponse,
    allUserWorkspacesData,
    workspaceData,
    workspaceDataOfUser,
    workspaceCreationDate,
    sortedWorkspaceMembers,
    workspaceMembersWithAdminRole,
    isSingleAdminInWorkspace,
    hasWorkspaceActiveSubscription,
    workspaceMembersWithMemberRole,
    workspaceMembersEmails,
    isWorkspaceEnterprise,
    pendingInvitations,
    userWorkspaceInvitedData,
    userWorkspaceInvitedBy,
    addMembersPopup,
    canUserSeeMembersListSection,
    setAddMembersPopup,
    removeMember,
    setRemoveMember,
    changeMemberRole,
    setChangeMemberRole,
  }
})
